import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { 
  Mail, Search, Filter, CheckCircle, XCircle, Clock, Eye,
  RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';

const EmailLog = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    email_type: '',
    recipient: '',
    status: ''
  });

  const limit = 50;

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmailLogs({
        skip: page * limit,
        limit,
        ...filters
      });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getEmailStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch email stats:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'delivered':
      case 'sent':
        return <Badge className="bg-green-500/20 text-green-400 border-0"><CheckCircle className="w-3 h-3 mr-1" /> Delivered</Badge>;
      case 'opened':
        return <Badge className="bg-blue-500/20 text-blue-400 border-0"><Eye className="w-3 h-3 mr-1" /> Opened</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-0"><XCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400 border-0"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-0">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      verification: 'bg-purple-500/20 text-purple-400',
      invitation: 'bg-blue-500/20 text-blue-400',
      record_notification: 'bg-cyan-500/20 text-cyan-400',
      digest: 'bg-green-500/20 text-green-400',
      trial_reminder: 'bg-amber-500/20 text-amber-400',
      contact: 'bg-pink-500/20 text-pink-400',
      password_reset: 'bg-red-500/20 text-red-400',
      general: 'bg-gray-500/20 text-gray-400'
    };
    return <Badge className={`${colors[type] || colors.general} border-0`}>{type?.replace('_', ' ')}</Badge>;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const emailTypes = [
    { value: '', label: 'All Types' },
    { value: 'verification', label: 'Verification' },
    { value: 'invitation', label: 'Invitation' },
    { value: 'record_notification', label: 'Record Notification' },
    { value: 'digest', label: 'Digest' },
    { value: 'trial_reminder', label: 'Trial Reminder' },
    { value: 'contact', label: 'Contact Form' },
    { value: 'password_reset', label: 'Password Reset' }
  ];

  const statuses = [
    { value: '', label: 'All Statuses' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'opened', label: 'Opened' },
    { value: 'failed', label: 'Failed' }
  ];

  const refreshActions = (
    <Button
      onClick={() => { fetchLogs(); fetchStats(); }}
      variant="outline"
      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
    >
      <RefreshCw className="w-4 h-4 mr-2" />
      Refresh
    </Button>
  );

  return (
    <AdminLayout title="Email Log" breadcrumbs={[{ label: 'Email Log' }]} actions={refreshActions}>
      <div className="space-y-6" data-testid="email-log">

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-blue-400" />
              <span className="text-[#5B6472] text-xs">Total Emails (30d)</span>
            </div>
            <p className="text-2xl font-bold text-[#0A0F1E]">{stats.total_emails || 0}</p>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-[#5B6472] text-xs">Delivered</span>
            </div>
            <p className="text-2xl font-bold text-[#0A0F1E]">{stats.delivered || 0}</p>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-purple-400" />
              <span className="text-[#5B6472] text-xs">Delivery Rate</span>
            </div>
            <p className="text-2xl font-bold text-[#0A0F1E]">{stats.delivery_rate || 100}%</p>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-4 h-4 text-cyan-400" />
              <span className="text-[#5B6472] text-xs">Email Types</span>
            </div>
            <p className="text-2xl font-bold text-[#0A0F1E]">{stats.by_type?.length || 0}</p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6472]" />
              <Input
                placeholder="Search by recipient email..."
                value={filters.recipient}
                onChange={(e) => setFilters({ ...filters, recipient: e.target.value })}
                className="pl-10 bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
              />
            </div>
          </div>

          <select
            value={filters.email_type}
            onChange={(e) => setFilters({ ...filters, email_type: e.target.value })}
            className="px-4 py-2 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5] text-[#0A0F1E] focus:border-[#1B5AFF] outline-none"
          >
            {emailTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 rounded-lg bg-[#FFFFFF] border border-[#E8EDF5] text-[#0A0F1E] focus:border-[#1B5AFF] outline-none"
          >
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Email Log Table */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-[#1B5AFF] animate-spin" />
          </div>
        ) : logs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FFFFFF] border-b border-[#E8EDF5]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Recipient</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Sent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#5B6472] uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8EDF5]">
                  {logs.map((log, index) => (
                    <tr key={log.id || index} className="hover:bg-[#FFFFFF]/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-[#0A0F1E] text-sm">{log.recipient}</span>
                      </td>
                      <td className="px-4 py-3">
                        {getTypeBadge(log.email_type)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[#5B6472] text-sm truncate max-w-xs block">{log.subject}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[#5B6472] text-sm">{formatDate(log.sent_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(log.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8EDF5]">
              <span className="text-[#5B6472] text-sm">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  variant="outline"
                  size="sm"
                  className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  variant="outline"
                  size="sm"
                  className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-16 text-[#5B6472]">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No email logs found</p>
            <p className="text-sm">Email logs will appear here when emails are sent</p>
          </div>
        )}
      </Card>
      </div>
    </AdminLayout>
  );
};

export default EmailLog;
